import React, { Component, Fragment } from 'react'
import { ApolloConsumer } from 'react-apollo'
import gql from 'graphql-tag'
import { PureQueryOptions } from 'apollo-client'
import { Action } from '../../../legacy-design-system/action'
import { DeleteDialog } from './delete-dialog'
import { Role, RoleSwitch, getLeastPermissiveRole } from '../../roles'
import { logError, logSuccess } from '../../toaster'
import { ContrastFormattedMessage } from '../../contrast-formatted-message'
import {
  IconDelete,
  IconSize
} from '@contrast-security-inc/design-system-foundations'
import { ActionButton } from '@contrast-security-inc/design-system-actions'
import { FormattedMessage } from 'react-intl'

export const actionTestID = 'vuln-delete-action'

interface Props {
  readonly organizationID: string
  readonly showDisabled?: boolean
  readonly vulnerabilities: Array<{
    readonly id: string
    readonly application: {
      readonly role: Role
    }
  }>
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
  readonly size?: IconSize
  readonly trackJob: (job: {
    readonly id: string
    readonly progress: number
    readonly total: number
  }) => Promise<void>
  readonly restrictedEditRole?: boolean
}

interface State {
  readonly dialogIsOpen: boolean
}

export const deleteVulnerabilitiesMutation = gql`
  mutation deleteVulnerabilities($organizationID: ID!, $ids: [ID!]!) {
    deleteVulnerabilities(
      input: { organizationID: $organizationID, ids: $ids }
    ) {
      jobStatus {
        id
        total
        progress
      }
    }
  }
`

export class DeleteAction extends Component<Props, State> {
  public readonly state: State = {
    dialogIsOpen: false
  }

  public render() {
    const { dialogIsOpen } = this.state
    const {
      vulnerabilities,
      organizationID,
      showDisabled,
      trackJob,
      refetchQueries,
      refresh,
      size,
      restrictedEditRole
    } = this.props
    const count = vulnerabilities.length
    const requiredRole = restrictedEditRole ? 'RULES_ADMIN' : 'EDIT'
    return (
      <ApolloConsumer>
        {(apolloClient) => {
          return (
            <ContrastFormattedMessage
              id="detail_vulnerabilities_successfullyDeletedVulnerability"
              children={(successMessage) => (
                <ContrastFormattedMessage
                  id="detail_vulnerabilities_errorDeletingVulnerability"
                  children={(errorMessage) => (
                    <Fragment>
                      <RoleSwitch
                        role={getLeastPermissiveRole(
                          vulnerabilities.map((v) => v.application.role)
                        )}
                        requiredRole={requiredRole}
                        renderDenied={
                          showDisabled
                            ? () => (
                                <ActionButton
                                  isDisabled
                                  TooltipProps={{
                                    body: (
                                      <FormattedMessage id="title_vulnerabilities_insufficientDeletePermissions" />
                                    )
                                  }}
                                  IconButtonProps={{
                                    Icon: IconDelete,
                                    'aria-label': 'Delete',
                                    size
                                  }}
                                  data-testid={actionTestID}
                                />
                              )
                            : undefined
                        }
                        renderAllowed={() => (
                          <Action
                            size={size}
                            data-testid={actionTestID}
                            iconTooltipMessage={{
                              id:
                                count === 1
                                  ? 'title_deleteVulnerability'
                                  : 'detail_vulnerabilities_deleteVulnerabilities'
                            }}
                            hideTooltip={dialogIsOpen}
                            icon={IconDelete}
                            iconTooltipPlacement="top"
                            onClick={() =>
                              this.setState({ dialogIsOpen: true })
                            }
                          />
                        )}
                      />
                      <DeleteDialog
                        isOpen={dialogIsOpen}
                        count={count}
                        onCancel={this.closeDialog}
                        onConfirm={async () => {
                          const result = await apolloClient.mutate({
                            mutation: deleteVulnerabilitiesMutation,
                            variables: {
                              ids: vulnerabilities.map((v) => v.id),
                              organizationID
                            }
                          })

                          this.closeDialog()

                          if (result && !result.errors && result.data) {
                            await trackJob(
                              result.data.deleteVulnerabilities.jobStatus
                            )
                            logSuccess(successMessage)
                          } else if (result && result.errors) {
                            logError(errorMessage)
                          }

                          await Promise.all(
                            refetchQueries.map(({ query, variables }) => {
                              // eslint-disable-next-line @typescript-eslint/no-floating-promises
                              apolloClient.query({
                                query,
                                variables,
                                fetchPolicy: 'network-only'
                              })
                            })
                          )

                          // eslint-disable-next-line @typescript-eslint/no-floating-promises
                          refresh()
                        }}
                      />
                    </Fragment>
                  )}
                />
              )}
            />
          )
        }}
      </ApolloConsumer>
    )
  }

  private closeDialog = () => {
    this.setState({ dialogIsOpen: false })
  }
}
